import { Component } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { WhitelabelService } from 'src/app/core/services/whitelabel.service';

// Full-screen loader while the whitelabel details load (WhitelabelService.detailsLoading$).
// No logo: a map pin hopping along a dotted route, in the biyaheko.com palette (navy ink,
// red pin, soft paper background). Colours are fixed because the organisation's theme is
// exactly what is still loading.
@Component({
  selector: 'app-whitelabel-loader',
  standalone: true,
  imports: [AsyncPipe],
  template: `
    @if (loading$ | async) {
    <div class="wl-loader" role="status" aria-live="polite">
      <div class="wl-loader__stage" aria-hidden="true">
        <svg class="wl-loader__route" viewBox="0 0 200 60">
          <path d="M10 50 Q 100 -10 190 50" fill="none" stroke="#C9CFE3" stroke-width="3"
            stroke-dasharray="1 9" stroke-linecap="round" />
          <circle cx="10" cy="50" r="5" fill="#1C2653" />
          <circle cx="190" cy="50" r="5" fill="#1C2653" />
        </svg>
        <div class="wl-loader__pin">
          <svg viewBox="0 0 24 32">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0Z" fill="#EC1C24" />
            <circle cx="12" cy="12" r="4.5" fill="#fff" />
          </svg>
        </div>
        <span class="wl-loader__shadow"></span>
      </div>
      <p class="wl-loader__text">Getting things ready<span class="wl-loader__dots"><i>.</i><i>.</i><i>.</i></span></p>
    </div>
    }
  `,
  styles: [`
    .wl-loader {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 18px;
      padding: 16px;
      background: radial-gradient(ellipse at top, #fff 0%, #F4F6FB 70%);
      font-family: Figtree, system-ui, sans-serif;
      animation: wl-fade-in .2s ease-out;
    }
    .wl-loader__stage {
      position: relative;
      width: 200px;
      height: 96px;
    }
    .wl-loader__route {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 200px;
      height: 60px;
    }
    .wl-loader__pin {
      position: absolute;
      left: 50%;
      top: 0;
      width: 30px;
      height: 40px;
      margin-left: -15px;
      animation: wl-hop .9s cubic-bezier(.5, 0, .5, 1) infinite;
      filter: drop-shadow(0 6px 8px rgba(236, 28, 36, .25));
    }
    .wl-loader__pin svg { width: 100%; height: 100%; display: block; }
    .wl-loader__shadow {
      position: absolute;
      left: 50%;
      top: 46px;
      width: 22px;
      height: 6px;
      margin-left: -11px;
      border-radius: 50%;
      background: rgba(28, 38, 83, .18);
      animation: wl-shadow .9s cubic-bezier(.5, 0, .5, 1) infinite;
    }
    .wl-loader__text {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: #4B5479;
      letter-spacing: .01em;
    }
    .wl-loader__dots i {
      font-style: normal;
      animation: wl-dot 1.2s infinite;
    }
    .wl-loader__dots i:nth-child(2) { animation-delay: .2s; }
    .wl-loader__dots i:nth-child(3) { animation-delay: .4s; }

    @keyframes wl-hop {
      0%, 100% { transform: translateY(6px) scale(1.04, .96); }
      50% { transform: translateY(-14px) scale(1, 1); }
    }
    @keyframes wl-shadow {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(.55); opacity: .5; }
    }
    @keyframes wl-dot {
      0%, 60%, 100% { opacity: .25; }
      30% { opacity: 1; }
    }
    @keyframes wl-fade-in { from { opacity: 0; } to { opacity: 1; } }
    @media (prefers-reduced-motion: reduce) {
      .wl-loader, .wl-loader__pin, .wl-loader__shadow, .wl-loader__dots i { animation: none; }
    }
  `]
})
export class WhitelabelLoaderComponent {
  readonly loading$: Observable<boolean>;

  constructor(whitelabelService: WhitelabelService) {
    this.loading$ = whitelabelService.detailsLoading$;
  }
}
